import { useCallback, useEffect, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import AgentMonitorService, {
  AgentMonitorAgent,
  AgentMonitorSummaryResponse
} from "../../../services/AgentMonitorService";
import AgentMonitorMetricCards from "./AgentMonitorMetricCards";
import AgentMonitorAgentsTable from "./AgentMonitorAgentsTable";
import ReassignDrawer from "./ReassignDrawer";
import { addIf } from "../../../utils/Utils";

const AgentMonitor = () => {
  const { translate } = TranslationService;

  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);
  const [summary, setSummary] = useState<AgentMonitorSummaryResponse | null>(
    null
  );

  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState(false);
  const [agents, setAgents] = useState<AgentMonitorAgent[]>([]);

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [drawerAgent, setDrawerAgent] = useState<AgentMonitorAgent | null>(
    null
  );

  const fetchAll = useCallback(async () => {
    setSummaryLoading(true);
    setAgentsLoading(true);
    setSummaryError(false);
    setAgentsError(false);

    const [summaryResult, agentsResult] = await Promise.all([
      AgentMonitorService.getSummary(),
      AgentMonitorService.getAgents()
    ]);

    setSummaryLoading(false);
    setAgentsLoading(false);

    if (summaryResult instanceof Error) {
      setSummaryError(true);
    } else {
      setSummary(summaryResult);
    }

    if (agentsResult instanceof Error) {
      setAgentsError(true);
    } else {
      setAgents(agentsResult);
    }

    setLastUpdated(new Date());
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const isLoading = summaryLoading || agentsLoading;

  const tableAgents: AgentMonitorAgent[] = [
    ...addIf(!!summary && !summaryError && summary.unassignedChats > 0, {
      agentId: null,
      firstName: "",
      lastName: "",
      agentName: translate.UnassignedAgentName || "Sin asignar",
      agentInitials: "SA",
      available: false,
      timeInStatus: undefined,
      openChats: summary?.unassignedChats ?? 0,
      chatsEnded: 0
    } as AgentMonitorAgent),
    ...agents
  ];

  const formatLastUpdated = (date: Date): string => {
    const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMin < 1) return translate.JustNow || "ahora";
    return `${translate.LastUpdatedAgo || "Actualizado hace"} ${diffMin} min`;
  };

  return (
    <div className="flex-fill p-4" style={{ overflowY: "auto", minWidth: 0 }}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <h5 className="mb-0" style={{ fontWeight: 600 }}>
            <i className="far fa-users me-2" style={{ color: "#0076b6" }} />
            {translate.AgentMonitor || "Monitor de agentes"}
          </h5>
          {lastUpdated && (
            <span className="text-muted" style={{ fontSize: "0.8rem" }}>
              {formatLastUpdated(lastUpdated)}
            </span>
          )}
        </div>
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          onClick={fetchAll}
          disabled={isLoading}
        >
          <i className={`far fa-sync-alt${isLoading ? " fa-spin" : ""}`} />
          {translate.Refresh || "Actualizar"}
        </button>
      </div>

      <AgentMonitorMetricCards
        summary={summary}
        loading={summaryLoading}
        error={summaryError}
        onRefresh={fetchAll}
      />

      <AgentMonitorAgentsTable
        agents={tableAgents}
        loading={agentsLoading}
        error={agentsError}
        onRefresh={fetchAll}
        onReassign={(agent) => setDrawerAgent(agent)}
      />

      {drawerAgent && (
        <ReassignDrawer
          agent={drawerAgent}
          agentDestinations={tableAgents}
          onClose={() => setDrawerAgent(null)}
          onSuccess={() => {
            setDrawerAgent(null);
            fetchAll();
          }}
        />
      )}
    </div>
  );
};

export default AgentMonitor;
